import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDetailSeance } from '../services/agendaService';
import { urlPdfDocument } from '../services/sessionService';
import FeuillesPresence from './FeuillesPresence';
import { formatDate, formatDateTime } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';
import '../assets/styles/SeanceDetail.css';
import { IconeOeil } from '../components/IconesDocument';

/** Les deux cases d'une journée, dans l'ordre de la feuille papier. */
const DEMI_JOURNEES = [
  { cle: 'matin', libelle: 'Matin' },
  { cle: 'apres_midi', libelle: 'Après-midi' },
];

/** « 16/09 » — l'en-tête d'une colonne d'émargement est étroit. */
function jourCourt(iso) {
  const [, m, j] = String(iso).split('-');
  return `${j}/${m}`;
}

const nomComplet = (p) => [p.prenom, p.nom].filter(Boolean).join(' ') || '—';

/** Le formateur porte le numéro 0 sur la feuille (participant fictif). */
const ID_FORMATEUR = 0;

/** Cases signées et cases à signer d'une feuille, formateur compris. */
function comptes(seance) {
  if (!seance.feuille) return { signees: 0, cases: 0 };
  const personnes = seance.participants.length + (seance.formateur ? 1 : 0);
  return {
    signees: seance.feuille.emargements.length,
    cases: personnes * seance.jours.length * DEMI_JOURNEES.length,
  };
}

/** L'état d'une feuille, en une pastille. */
function etatFeuille(seance) {
  if (!seance.feuille) return { libelle: 'feuille pas encore générée', classe: '' };
  const { signees, cases } = comptes(seance);
  if (cases > 0 && signees >= cases) return { libelle: 'entièrement émargée', classe: ' devis-pill-valide' };
  return {
    libelle: `${signees}/${cases} case${cases > 1 ? 's' : ''} signée${signees > 1 ? 's' : ''}`,
    classe: signees ? ' devis-pill-attente' : '',
  };
}

/** Le tableau d'une feuille : une ligne par personne, deux cases par journée. */
function TableauEmargement({ seance }) {
  const signee = (participantId, jour, demi) =>
    (seance.feuille?.emargements || []).find(
      (m) => m.participantId === Number(participantId) && m.jour === jour && m.demiJournee === demi
    );

  const lignes = [
    ...seance.participants.map((p) => ({ cle: `p-${p.id}`, id: p.id, nom: nomComplet(p), detail: p.societe || p.qualite, role: 'Participant' })),
    ...(seance.formateur
      ? [{ cle: 'formateur', id: ID_FORMATEUR, nom: seance.formateur.nom || '—', detail: seance.formateur.email, role: 'Formateur' }]
      : []),
  ];

  if (!lignes.length) {
    return <div className="clients-ligne-detail">Aucun participant inscrit sur cette séance.</div>;
  }

  return (
    <div className="seance-table-cadre">
      <table className="seance-table">
        <thead>
          <tr>
            <th rowSpan={2}>Personne</th>
            <th rowSpan={2}>Rôle</th>
            {seance.jours.map((jour) => (
              <th key={jour} colSpan={DEMI_JOURNEES.length} className="seance-th-jour" title={formatDate(jour)}>
                {jourCourt(jour)}
              </th>
            ))}
          </tr>
          <tr>
            {seance.jours.map((jour) =>
              DEMI_JOURNEES.map((d) => (
                <th key={`${jour}-${d.cle}`} className="seance-th-demi">
                  {d.libelle}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.cle} className={l.role === 'Formateur' ? 'seance-ligne-formateur' : undefined}>
              <td>
                <div className="seance-nom">{l.nom}</div>
                {l.detail && <div className="clients-ligne-detail">{l.detail}</div>}
              </td>
              <td>{l.role}</td>
              {seance.jours.map((jour) =>
                DEMI_JOURNEES.map((d) => {
                  const m = seance.feuille ? signee(l.id, jour, d.cle) : null;
                  return (
                    <td
                      key={`${jour}-${d.cle}`}
                      className={`seance-case${m ? ' seance-case-signee' : ''}`}
                      title={m ? `Signé le ${formatDateTime(m.signeLe)}` : 'Non signé'}
                    >
                      {m ? '✓' : '—'}
                    </td>
                  );
                })
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Le détail d'une session de formation, ouvert depuis l'agenda : toutes les
 * feuilles de présence qui s'y rattachent, chacune avec son formateur, ses
 * participants et ce qu'ils ont émargé.
 *
 * Une formation vendue a une feuille ; une session INTER du catalogue en a
 * une par client inscrit. La feuille elle-même (signature à l'ardoise,
 * envoi, correction) s'ouvre d'ici, dans le même panneau que partout.
 */
function SeanceDetail() {
  const { source, id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // La feuille ouverte dans le panneau : { contrat, documentId }.
  const [feuilleOuverte, setFeuilleOuverte] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getDetailSeance(source, id)
      .then((res) => setDetail(res.data))
      .catch((err) => setError(err.message || 'Session introuvable.'))
      .finally(() => setLoading(false));
  }, [source, id]);

  useEffect(() => {
    charger();
  }, [charger]);

  const seances = detail ? detail.seances : [];
  const nbFeuilles = seances.filter((s) => s.feuille).length;
  const nbParticipants = seances.reduce((n, s) => n + s.participants.length, 0);
  const formateurs = [...new Set(seances.map((s) => s.formateur?.nom).filter(Boolean))];

  return (
    <div className="devis-page devis-page-scroll">
      <div className="devis-header">
        <div>
          <div className="clients-dossier-tete">
            <button type="button" className="clients-retour" onClick={() => navigate('/calendrier')}>
              ← Calendrier
            </button>
          </div>
          <h1 className="devis-title">{detail ? detail.titre : 'Session de formation'}</h1>
          <p className="devis-subtitle">
            {detail
              ? [
                  detail.reference,
                  detail.jours.map((j) => formatDate(j)).join(' · '),
                  detail.heureDebut && detail.heureFin ? `${detail.heureDebut}–${detail.heureFin}` : null,
                  detail.lieu,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Feuilles de présence, participants et formateurs'}
          </p>
        </div>
      </div>

      {loading && <div className="devis-state">Chargement de la session...</div>}
      {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

      {!loading && detail && (
        <>
          <div className="devis-card clients-demande">
            <div className="seance-resume">
              <div>
                <div className="clients-fiche-libelle">Feuilles de présence</div>
                <div className="seance-resume-valeur">
                  {nbFeuilles}
                  {seances.length > nbFeuilles ? ` sur ${seances.length} séance${seances.length > 1 ? 's' : ''}` : ''}
                </div>
              </div>
              <div>
                <div className="clients-fiche-libelle">Participants</div>
                <div className="seance-resume-valeur">{nbParticipants}</div>
              </div>
              <div>
                <div className="clients-fiche-libelle">Formateur{formateurs.length > 1 ? 's' : ''}</div>
                <div className="seance-resume-valeur">{formateurs.join(', ') || 'non assigné'}</div>
              </div>
              {detail.catalogue && (
                <div>
                  <div className="clients-fiche-libelle">Session en inter</div>
                  <div className="seance-resume-valeur">
                    {detail.catalogue.placesReservees}/{detail.catalogue.placesMax} places
                  </div>
                </div>
              )}
            </div>
          </div>

          {seances.length === 0 && (
            <div className="devis-card clients-vide">
              Aucun client n’est encore planifié sur cette session : pas de feuille de présence.
              Elles apparaissent ici dès qu’un contrat y rattache une séance.
            </div>
          )}

          {seances.map((s) => {
            const etat = etatFeuille(s);
            return (
              <div className="devis-card clients-demande" key={s.id}>
                <div className="clients-demande-tete">
                  <div>
                    <div className="clients-demande-titre">
                      {s.societeNom}
                      {s.groupeLibelle ? ` — ${s.groupeLibelle}` : ''}
                    </div>
                    <div className="clients-demande-sous">
                      {[
                        s.intitule,
                        `contrat ${s.contratNumero}`,
                        s.jours.map((j) => formatDate(j)).join(', '),
                        s.modalite === 'distanciel' ? 'À distance' : s.lieu,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <div className="seance-actions">
                    <span className={`devis-status-pill${etat.classe}`}>{etat.libelle}</span>
                    {s.feuille && (
                      <>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-primary"
                          onClick={() =>
                            setFeuilleOuverte({
                              contrat: { id: s.contratId, numero: s.contratNumero, societe_nom: s.societeNom },
                              documentId: s.feuille.id,
                            })
                          }
                        >
                          Ouvrir la feuille
                        </button>
                        <a
                          className="devis-btn devis-btn-sm"
                          href={urlPdfDocument(s.feuille.id, { apercu: true })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconeOeil />
                          PDF
                        </a>
                      </>
                    )}
                    <button
                      type="button"
                      className="devis-btn devis-btn-sm"
                      onClick={() => navigate(`/clients/${s.contactId}`)}
                    >
                      Dossier client
                    </button>
                  </div>
                </div>

                <div className="clients-section">
                  <div className="clients-section-titre">Formateur</div>
                  {s.formateur ? (
                    <div className="clients-ligne">
                      <div className="clients-ligne-texte">
                        <div>{s.formateur.nom || '—'}</div>
                        {s.formateur.email && <div className="clients-ligne-detail">{s.formateur.email}</div>}
                      </div>
                      {s.formateur.id && (
                        <div className="clients-ligne-actions">
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm"
                            onClick={() => navigate(`/formateurs/${s.formateur.id}`)}
                          >
                            Fiche du formateur
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="clients-ligne-detail">
                      Aucun formateur assigné (assignation dans la planification du contrat).
                    </div>
                  )}
                </div>

                <div className="clients-section">
                  <div className="clients-section-titre">
                    {s.feuille ? 'Feuille de présence' : 'Participants prévus'} — {s.participants.length}{' '}
                    participant{s.participants.length > 1 ? 's' : ''}
                  </div>
                  <TableauEmargement seance={s} />
                  {s.feuille && (
                    <div className="clients-ligne-detail seance-feuille-pied">
                      Feuille générée le {formatDateTime(s.feuille.genereLe)}
                      {s.feuille.envoyeeLe ? ` · envoyée au client le ${formatDateTime(s.feuille.envoyeeLe)}` : ''}
                    </div>
                  )}
                  {!s.feuille && (
                    <div className="clients-ligne-detail seance-feuille-pied">
                      La feuille de présence se génère depuis la planification du contrat.
                    </div>
                  )}
                  {s.feuille?.notesFormateur && (
                    <div className="seance-notes">
                      <div className="clients-fiche-libelle">Notes du formateur</div>
                      {s.feuille.notesFormateur}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {feuilleOuverte && (
        <FeuillesPresence
          contrat={feuilleOuverte.contrat}
          documentId={feuilleOuverte.documentId}
          ouvrirDirectement
          onClose={() => {
            setFeuilleOuverte(null);
            // Une case a pu être signée dans le panneau : les comptes suivent.
            charger();
          }}
        />
      )}
    </div>
  );
}

export default SeanceDetail;
